import { gql, useLazyQuery, useMutation } from '@apollo/client'
import {
  CreateNewProjectMutation,
  CreateNewProjectMutationVariables,
  CreateProjectApplicationStripeCheckoutMutation,
  CreateProjectApplicationStripeCheckoutMutationVariables,
  CreateProjectAssetMutation,
  CreateProjectAssetMutationVariables,
  DeleteProjectAssetMutation,
  DeleteProjectAssetMutationVariables,
  GetProjectFlowQuery,
  GetProjectFlowQueryVariables,
  InviteWithProjectAssociationMutation,
  InviteWithProjectAssociationMutationVariables,
  UpdateGuildUpdateForAssetMutation,
  UpdateGuildUpdateForAssetMutationVariables,
  UpdateProjectAssetDetailsMutation,
  UpdateProjectAssetDetailsMutationVariables,
  UpdateProjectAssetMutation,
  UpdateProjectAssetMutationVariables,
  UpdateProjectDocumentMutation,
  UpdateProjectDocumentMutationVariables,
  UpdateProjectMutation,
  UpdateProjectMutationVariables,
  UpsertProjectAssociationMutation,
  UpsertProjectAssociationMutationVariables,
  UpsertProjectContactMutation,
  UpsertProjectContactMutationVariables,
} from '@/types/codegen-federation'
import { getWebClient } from '../ApolloClient/ApolloClient'

const localClient = getWebClient({ baseUrl: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL as string })

export const CREATE_PROJECT = gql`
  mutation createNewProject($input: createProjectInput) {
    createProject(input: $input) {
      id
      name
    }
  }
`

export const UPDATE_PROJECT = gql`
  mutation updateProject($input: updateProjectInput) {
    updateProject(input: $input) {
      id
    }
  }
`

export const UPDATE_PROJECT_ASSET = gql`
  mutation updateProjectAsset($input: UpdateProjectAssetInput!) {
    updateProjectAsset(input: $input) {
      id
    }
  }
`

export const CREATE_PROJECT_ASSET = gql`
  mutation createProjectAsset($input: CreateProjectAssetInput!) {
    createProjectAsset(input: $input) {
      id
    }
  }
`

export const DELETE_PROJECT_ASSET = gql`
  mutation deleteProjectAsset($input: DeleteProjectAssetInput!) {
    deleteProjectAsset(input: $input)
  }
`

export const UPDATE_PROJECT_ASSET_DETAILS = gql`
  mutation updateProjectAssetDetails($input: UpdateProjectAssetDetailsInput!) {
    updateProjectAssetDetails(input: $input)
  }
`

export const UPSERT_PROJECT_CONTACT = gql`
  mutation upsertProjectContact($input: UpsertProjectContactInput!) {
    upsertProjectContact(input: $input) {
      id
    }
  }
`

export const UPSERT_PROJECT_ASSOC = gql`
  mutation upsertProjectAssociation($input: UpsertProjectAssociationInput!) {
    upsertProjectAssociation(input: $input) {
      joinCode
    }
  }
`

export const INVITE_TO_PROJECT = gql`
  mutation inviteWithProjectAssociation($input: UpdateTorchProjectAssociationInput) {
    updateTorchProjectAssociation(input: $input) {
      joinCode
    }
  }
`

export const CREATE_PROJECT_APPLICATION_STRIPE_CHECKOUT = gql`
  mutation createProjectApplicationStripeCheckout($input: ProjectApplicationStripeCheckoutInput!) {
    createProjectApplicationStripeCheckout(input: $input) {
      clientSecret
    }
  }
`

export const GET_PROJECT_PAYMENT_STATUS = gql`
  query getProjectPaymentStatus($input: GuildProjectCheckoutStatusInput!) {
    guildProjectCheckoutStatus(input: $input) {
      status
      paymentStatus
      email
    }
  }
`

export const UPDATE_GUILD_UPDATE = gql`
  mutation updateGuildUpdateForAsset($input: UpdateGuildUpdateForAssetInput!) {
    updateGuildUpdateForAsset(input: $input)
  }
`

export const GET_GUILD_PROJECTS = gql`
  query getGuildProjects($name: String) {
    getGuildProjects(name: $name) {
      id
      name
      productionType
      orderType
      canUpload
      projectAssets {
        id
        alias
        title
        status
        stage
        acknowledgement
        age
        genres
        audience
        presentationType
        description
        allowShares
        predistributed
        predistributedDesc
        guildUpdate {
          id
          creatorName
          creatorTitle
          videoAspectRatio
          status
        }
        projectPayment {
          isPaid
          paidAt
          amount
          subtotal
          discount
          tax
          stripeSessionId
          stripeInvoiceNr
          stripeInvoiceUrl
        }
        episode {
          id
          episodeId
          name
          season {
            id
            seasonId
          }
        }
        createdAt
        bucket {
          id
          name
          url
          type
        }
        urls {
          id
          name
          url
          type
        }
        torches {
          id
          title
          filmmakerAssetUrl
          latestNormalizedScore
          latestConfidence
          posterCloudinaryPath
          torchTags {
            id
            name
            type
          }
          torchEvents {
            id
            action
            status
            type
          }
          metadata {
            isKids
            currentStatus
            category
            phases {
              application {
                status
              }
              defenders {
                status
              }
              guild {
                status
              }
            }
          }
        }
      }
      seasons {
        id
        seasonId
        episodes {
          id
          episodeId
          name
        }
      }
      projectDocuments {
        id
        type
        createdAt
        urls {
          id
          type
          name
          url
        }
        uploadUrl
        signatureUploadUrl
        events {
          id
          eventType
          createdBy
          createdByIP
          createdFor
          createdAt
        }
      }
      projectAssociations {
        name
        status
        userId
        email
        phone
        associationType
        isSigner
        isUploader
      }
      contact {
        id
        companyName
        companyRole
        addressLine1
        city
        state
        zipcode
        country
        isIndividual
        userId
        confirmed
      }
      projectApplication {
        id
        status
        submittedAt
        signatureDate
        urls {
          id
          type
          name
          url
        }
      }
    }
  }
`

export const PROJECT_FLOW = gql`
  query getProjectFlow($id: ID!, $assetId: ID) {
    guildProjectFlow(id: $id, assetId: $assetId) {
      tasks {
        name
        status
        order
        skip
      }
      nextTask {
        name
        status
        order
        skip
      }
      assetTasks {
        id
        tasks {
          name
          status
          order
          skip
        }
        nextTask {
          name
          status
          order
          skip
        }
      }
      progress
      flowState
      project {
        id
        name
        productionType
        orderType
        canUpload
        projectAssociations {
          name
          status
          userId
          email
          phone
          associationType
          isSigner
          isUploader
        }
        projectDocuments {
          id
          type
          createdAt
          urls {
            id
            type
            name
            url
          }
          uploadUrl
          signatureUploadUrl
          events {
            id
            eventType
            createdBy
            createdByIP
            createdFor
            createdAt
          }
        }
        contact {
          id
          companyName
          companyRole
          addressLine1
          city
          state
          zipcode
          country
          isIndividual
          confirmed
        }
        projectApplication {
          id
          signatureDate
          submittedAt
          status
          urls {
            id
            type
            name
            url
          }
        }
        projectAssets {
          id
          alias
          title
          status
          stage
          acknowledgement
          age
          genres
          audience
          presentationType
          description
          allowShares
          predistributed
          predistributedDesc
          guildUpdate {
            id
            creatorName
            creatorTitle
            videoAspectRatio
            status
          }
          projectPayment {
            isPaid
            paidAt
            amount
            subtotal
            discount
            tax
            stripeSessionId
            stripeInvoiceNr
            stripeInvoiceUrl
          }
          episode {
            id
            episodeId
            name
            season {
              id
              seasonId
            }
          }
          createdAt
          bucket {
            id
            name
            url
            type
          }
          urls {
            id
            name
            url
            type
          }
        }
      }
      pendingAssets {
        id
        alias
        title
        status
        stage
        acknowledgement
        age
        genres
        audience
        presentationType
        description
        allowShares
        predistributed
        predistributedDesc
        episode {
          id
          episodeId
          name
          season {
            id
            seasonId
          }
        }
        bucket {
          id
          type
          name
          url
        }
      }
    }
  }
`

export const USER_PROJECTS_FROM_ASSOCIATION = gql`
  query getUserProjectsFromAssoication {
    torchProjects {
      name
      id
      associations {
        projectId
        userId
        associationType
        joinCode
        status
        email
        name
      }
    }
  }
`

export const GET_GUILD_PROJECT_ADMIN = gql`
  query getGuildProjectAdminFromAction {
    guildUserActions {
      isGuildProjectAdmin
    }
  }
`

export const UPDATE_PROJECT_DOCUMENT = gql`
  mutation updateProjectDocument($input: GuildProjectDocumentInput!) {
    updateProjectDocument(input: $input) {
      id
      type
      createdAt
      urls {
        id
        type
        name
        url
      }
      uploadUrl
      signatureUploadUrl
      events {
        id
        eventType
        createdBy
        createdByIP
        createdFor
        createdAt
      }
    }
  }
`

export const useGuildProjectAdmin = () => {
  return useLazyQuery(GET_GUILD_PROJECT_ADMIN, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })
}

export const useUserProjectsFromAssociation = () => {
  const [getUserProjects] = useLazyQuery(USER_PROJECTS_FROM_ASSOCIATION, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })
  return getUserProjects
}

export const useCreateNewProjectMutation = () => {
  const [mutate] = useMutation<CreateNewProjectMutation, CreateNewProjectMutationVariables>(CREATE_PROJECT, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
  })

  return mutate
}

export const useUpdateProjectMutation = () => {
  const [mutate] = useMutation<UpdateProjectMutation, UpdateProjectMutationVariables>(UPDATE_PROJECT, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
  })

  return mutate
}

export const useUpdateProjectAssetMutation = () => {
  const [mutate] = useMutation<UpdateProjectAssetMutation, UpdateProjectAssetMutationVariables>(UPDATE_PROJECT_ASSET, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
  })
  return mutate
}

export const useCreateProjectAssetMutation = () => {
  const [mutate] = useMutation<CreateProjectAssetMutation, CreateProjectAssetMutationVariables>(CREATE_PROJECT_ASSET, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
  })
  return mutate
}

export const useDeleteProjectAssetMutation = () => {
  const [mutate] = useMutation<DeleteProjectAssetMutation, DeleteProjectAssetMutationVariables>(DELETE_PROJECT_ASSET, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
  })
  return mutate
}

export const useUpdateProjectAssetDetails = () => {
  const [mutate] = useMutation<UpdateProjectAssetDetailsMutation, UpdateProjectAssetDetailsMutationVariables>(
    UPDATE_PROJECT_ASSET_DETAILS,
    {
      client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    },
  )
  return mutate
}

export const useUpsertProjectContact = () => {
  const [mutate] = useMutation<UpsertProjectContactMutation, UpsertProjectContactMutationVariables>(
    UPSERT_PROJECT_CONTACT,
    {
      client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    },
  )
  return mutate
}

export const useUpsertProjectAssociation = () => {
  const [mutate] = useMutation<UpsertProjectAssociationMutation, UpsertProjectAssociationMutationVariables>(
    UPSERT_PROJECT_ASSOC,
    {
      client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    },
  )
  return mutate
}

export const useInviteWithProjectAssociation = () => {
  const [mutate] = useMutation<InviteWithProjectAssociationMutation, InviteWithProjectAssociationMutationVariables>(
    INVITE_TO_PROJECT,
    {
      client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    },
  )
  return mutate
}

export const useProjectCheckoutStatus = () => {
  const [projectPaymentStatus] = useLazyQuery(GET_PROJECT_PAYMENT_STATUS, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })

  return projectPaymentStatus
}

export const useUpdateGuildUpdate = () => {
  const [mutate] = useMutation<UpdateGuildUpdateForAssetMutation, UpdateGuildUpdateForAssetMutationVariables>(
    UPDATE_GUILD_UPDATE,
    {
      client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    },
  )

  return mutate
}

export const useProjectApplicationCheckout = () => {
  const [mutate] = useMutation<
    CreateProjectApplicationStripeCheckoutMutation,
    CreateProjectApplicationStripeCheckoutMutationVariables
  >(CREATE_PROJECT_APPLICATION_STRIPE_CHECKOUT, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
  })
  return mutate
}

export const useProjectDocumentUpdate = () => {
  const [mutate] = useMutation<UpdateProjectDocumentMutation, UpdateProjectDocumentMutationVariables>(
    UPDATE_PROJECT_DOCUMENT,
    {
      client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    },
  )
  return mutate
}

export const useGuildProjects = () => {
  const [getProjects] = useLazyQuery(GET_GUILD_PROJECTS, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })
  return getProjects
}

export const useProjectFlow = () => {
  const [projectFlow] = useLazyQuery<GetProjectFlowQuery, GetProjectFlowQueryVariables>(PROJECT_FLOW, {
    client: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : undefined,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  })

  return projectFlow
}
